import {
  ApiError,
  ApiErrorInitialState,
  AssetEntity,
  AssetWithOwnersEntity,
  GinApplication,
  GinApplicationRelatedAssetsEntity,
  UploadAssetResponse,
} from '@hellodarwin/core/lib/features/entities';
import {
  getGinStepAssignedContactIds,
  GinStep,
  GinStepApplicationItem,
  GinStepForm,
  GinStepItem,
  GinStepPage,
  GinStepTask,
} from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi, { GinStepDocumentRequest } from '../client-api';
import ClientApiV2 from '../client-api-v2';

const ginStepAssetsAdapter = createEntityAdapter<AssetEntity, string>({
  selectId: (model: AssetEntity) => model.asset_id,
});
const ginStepTasksAdapter = createEntityAdapter<GinStepTask, string>({
  selectId: (model: GinStepTask) => model.gin_step_id,
});

const updateAssetInGinStepForm = (
  form: GinStepForm | undefined,
  asset: AssetWithOwnersEntity,
): GinStepForm | undefined => {
  if (!form) {
    return form;
  }

  const updateItem = (item: GinStepItem): GinStepItem => {
    if (
      item.application_item?.item_asset_application?.file_response?.asset_id ===
      asset.asset_id
    ) {
      return {
        ...item,
        application_item: {
          ...item.application_item,
          item_asset_application: {
            ...item.application_item.item_asset_application,
            file_response: asset,
          },
        },
      };
    }

    if (item.application_item?.asset_bucket_application?.file_responses) {
      return {
        ...item,
        application_item: {
          ...item.application_item,
          asset_bucket_application: {
            ...item.application_item.asset_bucket_application,
            file_responses:
              item.application_item.asset_bucket_application.file_responses.map(
                (fileResponse) =>
                  fileResponse.asset_id === asset.asset_id
                    ? asset
                    : fileResponse,
              ),
          },
        },
      };
    }

    return item;
  };

  const updatedPages = form.pages?.map((page) => ({
    ...page,
    steps: page.steps?.map((step) => ({
      ...step,
      items: step.items?.map(updateItem),
    })),
  }));

  const updatedItemsMap = form.items_map
    ? Object.fromEntries(
        Object.entries(form.items_map).map(([itemId, item]) => [
          itemId,
          updateItem(item),
        ]),
      )
    : form.items_map;

  return {
    ...form,
    pages: updatedPages,
    items_map: updatedItemsMap,
  };
};

export const fetchIsClientFacing = createAsyncThunk<
  boolean,
  { api: ClientApiV2; programId: string },
  { rejectValue: ApiError }
>(
  'client/fetchIsClientFacing',
  async (
    { api, programId }: { api: ClientApiV2; programId: string },
    { rejectWithValue },
  ) => {
    try {
      const resp = await api.createAxios().get<{
        isClientFacing: boolean;
      }>(`/programs/${programId}/client-facing`);
      return resp.data.isClientFacing;
    } catch (err: any) {
      console.error(err.response?.data || err);
      showErrorNotification(err.response?.data || err);
      return rejectWithValue(err.response?.data || err);
    }
  },
);

export interface GinFormState {
  status: 'idle' | 'pending';
  error: ApiError;
  ginStepForm: GinStepForm | undefined;
  ginStepAssets: EntityState<AssetEntity, string>;
  ginStepAssetsStatus: 'idle' | 'pending';
  loadingItems: { [itemId: string]: boolean };
  ginStepTasks: EntityState<GinStepTask, string>;
  documentRequests: GinStepDocumentRequest[];
  documentRequestsStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  applicationItemCreated: boolean;
}

const initialState: GinFormState = {
  status: 'idle',
  error: ApiErrorInitialState,
  ginStepForm: undefined,
  ginStepAssets: ginStepAssetsAdapter.getInitialState(),
  ginStepAssetsStatus: 'idle',
  loadingItems: {},
  ginStepTasks: ginStepTasksAdapter.getInitialState(),
  documentRequests: [],
  documentRequestsStatus: 'idle',
  applicationItemCreated: false,
};

export const fetchGinStepForm = createAsyncThunk<
  GinStepForm,
  { api: ClientApiV2; programId: string; ownerType: string | '' },
  { rejectValue: ApiError }
>(
  'client/fetchGinStepForm',
  async (
    {
      api,
      programId,
      ownerType,
    }: { api: ClientApiV2; programId: string; ownerType: string | '' },
    { rejectWithValue },
  ) => {
    try {
      const resp = await api
        .createAxios()
        .get(
          `/ginStep/application/program/${programId}?isSelfServe=true&ownerType=${ownerType}`,
        );
      return resp.data;
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const createGinStepFormApplicationItem = createAsyncThunk<
  GinStepForm,
  {
    api: ClientApiV2;
    ginStepFormApplicationItem: GinStepApplicationItem;
    programId: string;
  },
  { rejectValue: ApiError }
>(
  'client/createGinStepFormApplicationItem',
  async (
    { api, ginStepFormApplicationItem, programId },
    { rejectWithValue },
  ) => {
    try {
      const resp = await api
        .createAxios()
        .post<GinStepForm>(
          `/ginStep/applicationItem?programId=${programId}`,
          ginStepFormApplicationItem,
        );
      return resp.data;
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateGinStepFormApplicationItem = createAsyncThunk<
  GinStepForm,
  { api: ClientApiV2; item: GinStepApplicationItem; programId: string },
  { rejectValue: ApiError }
>(
  'client/updateGinStepFormApplicationItem',
  async ({ api, item, programId }, { rejectWithValue }) => {
    try {
      const ginStepFormApplicationItemResponse = await api
        .createAxios()
        .put<GinStepForm>(
          `/ginStep/applicationItem?programId=${programId}`,
          item,
        );
      return ginStepFormApplicationItemResponse.data;
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteGinStepFormApplicationItem = createAsyncThunk<
  GinStepForm,
  {
    api: ClientApiV2;
    ginStepApplicationItem: GinStepApplicationItem;
    programId: string;
  },
  { rejectValue: ApiError }
>(
  'client/deleteGinStepFormApplicationItem',
  async ({ api, ginStepApplicationItem, programId }, { rejectWithValue }) => {
    try {
      const ginStepFormApplicationItemResponse = await api
        .createAxios()
        .delete<GinStepForm>(
          `/ginStep/applicationItem?programId=${programId}`,
          { data: ginStepApplicationItem },
        );
      return ginStepFormApplicationItemResponse.data;
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateGinStepProperty = createAsyncThunk<
  { form: GinStepForm; application: GinApplication },
  {
    api: ClientApiV2;
    value: string | string[];
    ginStepItem: GinStepItem;
    appItem: GinStepApplicationItem;
  },
  { rejectValue: ApiError }
>(
  'client/updateGinStepProperty',
  async (
    {
      api,
      value,
      ginStepItem,
      appItem,
    }: {
      api: ClientApiV2;
      value: string | string[];
      ginStepItem: GinStepItem;
      appItem: GinStepApplicationItem;
    },
    { rejectWithValue },
  ) => {
    try {
      const ginStepItemResponse = await api.put<{
        form: GinStepForm;
        application: GinApplication;
      }>('/ginStep/application/property', {
        ginStepItem,
        appItem,
        value,
      });
      return ginStepItemResponse.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const validateGinStep = createAsyncThunk<
  GinStepForm,
  { api: ClientApi; programId: string; ginStepId: string },
  { rejectValue: ApiError }
>(
  'client/validateGinStep',
  async (
    {
      api,
      programId,
      ginStepId,
    }: {
      api: ClientApi;
      programId: string;
      ginStepId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const ginStepFormApplicationResponse = await api.validateGinStep(
        programId,
        ginStepId,
      );
      return ginStepFormApplicationResponse;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

// Asset-related async thunks
export const renameApplicationAsset = createAsyncThunk<
  { asset: AssetWithOwnersEntity },
  {
    api: ClientApiV2;
    asset: AssetWithOwnersEntity;
    programId: string;
  },
  { rejectValue: ApiError }
>(
  'client/renameApplicationAsset',
  async ({ api, asset, programId }, { rejectWithValue }) => {
    try {
      const response = await api
        .createAxios()
        .put<AssetWithOwnersEntity>(
          `/ginStep/application/assets/${asset.asset_id}/rename?programId=${programId}`,
          {
            display_name: asset.display_name,
            description: asset.description,
            bundle_id: asset.bundle_id || '',
          },
        );
      return { asset: response.data };
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGinStepAssets = createAsyncThunk<
  AssetEntity[],
  { api: ClientApiV2; programId: string },
  { rejectValue: ApiError }
>(
  'client/fetchGinStepAssets',
  async ({ api, programId }, { rejectWithValue }) => {
    try {
      const response = await api
        .createAxios()
        .get<AssetEntity[]>(`/ginStep/application/assets/${programId}`);
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const copyApplicationAsset = createAsyncThunk<
  AssetWithOwnersEntity,
  {
    api: ClientApiV2;
    asset: UploadAssetResponse;
  },
  { rejectValue: ApiError }
>(
  'client/copyApplicationAsset',
  async ({ api, asset }, { rejectWithValue }) => {
    try {
      const assetResponse = await api
        .createAxios()
        .post<AssetWithOwnersEntity>(`/assets/copy`, asset);
      return assetResponse.data;
    } catch (err: any) {
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGinStepApplicationRelatedAssets = createAsyncThunk<
  GinApplicationRelatedAssetsEntity,
  {
    api: ClientApiV2;
    locale: string;
    assetType?: string;
    excludeProgramId?: string;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGinStepApplicationRelatedAssets',
  async ({ api, locale, assetType, excludeProgramId }, { rejectWithValue }) => {
    try {
      const ginStepApplicationRelatedAssetsResponse = await api
        .createAxios()
        .get<GinApplicationRelatedAssetsEntity>(
          '/ginStep/application/relatedAssets',
          { params: { locale, assetType, excludeProgramId } },
        );
      return ginStepApplicationRelatedAssetsResponse.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteApplicationAssets = createAsyncThunk<
  { assetIds: string[]; ginStepForm: GinStepForm | null },
  {
    api: ClientApiV2;
    asset_ids: string[];
    program_id?: string;
    app_item_id?: string;
  },
  { rejectValue: ApiError }
>(
  'client/deleteApplicationAsset',
  async ({ api, asset_ids, program_id, app_item_id }, { rejectWithValue }) => {
    try {
      const response = await api.delete<GinStepForm>(`/ginStep/assets`, {
        data: { asset_ids, program_id, app_item_id },
      });
      return { assetIds: asset_ids, ginStepForm: response.data };
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGinStepTasks = createAsyncThunk<
  GinStepTask[],
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/fetchGinStepTasks',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.fetchGinStepTasks();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGinStepDocumentRequests = createAsyncThunk<
  GinStepDocumentRequest[],
  { api: ClientApi; locale: string },
  { rejectValue: ApiError }
>(
  'client/fetchGinStepDocumentRequests',
  async ({ api, locale }, { rejectWithValue }) => {
    try {
      return await api.fetchGinStepDocumentRequests(locale);
    } catch (err: any) {
      showErrorNotification(err.response?.data?.error_code);
      return rejectWithValue(err.response?.data || err);
    }
  },
);

// Slice
export const ginStepsSlice = createSlice({
  name: 'ginSteps',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGinStepForm.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchGinStepForm.fulfilled, (state, action) => {
        state.status = 'idle';
        state.ginStepForm = action.payload;
      })
      .addCase(fetchGinStepForm.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload || ApiErrorInitialState;
      })
      // Create application item
      .addCase(createGinStepFormApplicationItem.pending, (state, action) => {
        const itemId = action.meta.arg.ginStepFormApplicationItem.item_id;
        state.loadingItems[itemId] = true;
      })
      .addCase(createGinStepFormApplicationItem.fulfilled, (state, action) => {
        state.ginStepForm = action.payload;
        const itemId = action.meta.arg.ginStepFormApplicationItem.item_id;
        state.loadingItems[itemId] = false;
        state.applicationItemCreated = true;
      })
      .addCase(createGinStepFormApplicationItem.rejected, (state, action) => {
        const itemId = action.meta.arg.ginStepFormApplicationItem.item_id;
        state.loadingItems[itemId] = false;
        state.error = action.payload || ApiErrorInitialState;
      })
      // Update application item
      .addCase(updateGinStepFormApplicationItem.pending, (state, action) => {
        const itemId = action.meta.arg.item.item_id;
        state.loadingItems[itemId] = true;
      })
      .addCase(updateGinStepFormApplicationItem.fulfilled, (state, action) => {
        state.ginStepForm = action.payload;
        const itemId = action.meta.arg.item.item_id;
        state.loadingItems[itemId] = false;
      })
      .addCase(updateGinStepFormApplicationItem.rejected, (state, action) => {
        const itemId = action.meta.arg.item.item_id;
        state.loadingItems[itemId] = false;
        state.error = action.payload || ApiErrorInitialState;
      })
      // Delete application item
      .addCase(deleteGinStepFormApplicationItem.pending, (state, action) => {
        const itemId = action.meta.arg.ginStepApplicationItem.item_id;
        state.loadingItems[itemId] = true;
      })
      .addCase(deleteGinStepFormApplicationItem.fulfilled, (state, action) => {
        state.ginStepForm = action.payload;
        const itemId = action.meta.arg.ginStepApplicationItem.item_id;
        state.loadingItems[itemId] = false;
      })
      .addCase(deleteGinStepFormApplicationItem.rejected, (state, action) => {
        const itemId = action.meta.arg.ginStepApplicationItem.item_id;
        state.loadingItems[itemId] = false;
        state.error = action.payload || ApiErrorInitialState;
      })
      .addCase(renameApplicationAsset.fulfilled, (state, action) => {
        state.ginStepAssets = ginStepAssetsAdapter.upsertOne(
          state.ginStepAssets,
          action.payload.asset,
        );
        state.ginStepForm = updateAssetInGinStepForm(
          state.ginStepForm,
          action.payload.asset,
        );
      })
      .addCase(updateGinStepProperty.pending, (state, { meta }) => {
        state.loadingItems[meta.arg.ginStepItem.item_id] = true;
      })
      .addCase(updateGinStepProperty.fulfilled, (state, { payload, meta }) => {
        state.ginStepForm = payload.form;
        state.loadingItems[meta.arg.ginStepItem.item_id] = false;
      })
      .addCase(updateGinStepProperty.rejected, (state, { payload, meta }) => {
        state.loadingItems[meta.arg.ginStepItem.item_id] = false;
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(fetchGinStepAssets.pending, (state) => {
        state.ginStepAssetsStatus = 'pending';
      })
      .addCase(fetchGinStepAssets.fulfilled, (state, { payload }) => {
        state.ginStepAssets = ginStepAssetsAdapter.setAll(
          state.ginStepAssets,
          payload ?? [],
        );
        state.ginStepAssetsStatus = 'idle';
      })
      .addCase(fetchGinStepAssets.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.ginStepAssetsStatus = 'idle';
      })
      .addCase(fetchGinStepTasks.fulfilled, (state, { payload }) => {
        if (payload) {
          state.ginStepTasks = ginStepTasksAdapter.setAll(
            state.ginStepTasks,
            payload,
          );
          state.status = 'idle';
        } else {
          state.ginStepTasks = ginStepTasksAdapter.removeAll(
            state.ginStepTasks,
          );
          state.status = 'idle';
        }
      })
      .addCase(fetchGinStepTasks.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      })
      .addCase(fetchGinStepDocumentRequests.pending, (state) => {
        state.documentRequests = [];
        state.documentRequestsStatus = 'pending';
      })
      .addCase(fetchGinStepDocumentRequests.fulfilled, (state, { payload }) => {
        state.documentRequests = payload ?? [];
        state.documentRequestsStatus = 'succeeded';
      })
      .addCase(fetchGinStepDocumentRequests.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.documentRequestsStatus = 'failed';
      })
      .addCase(validateGinStep.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(validateGinStep.fulfilled, (state, { payload }) => {
        state.ginStepForm = payload;
        state.status = 'idle';
      })
      .addCase(validateGinStep.rejected, (state, { payload }) => {
        state.status = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      });
  },
});
// Basic selectors
export const selectGinStepsIsLoading = (state: RootState) =>
  state.ginSteps.status === 'pending';

export const selectGinStepsError = (state: RootState) => state.ginSteps.error;

export const selectGinStepDocumentRequests = (state: RootState) =>
  state.ginSteps.documentRequests;

export const selectGinStepDocumentRequestsStatus = (state: RootState) =>
  state.ginSteps.documentRequestsStatus;

export const selectGinStepForm = (state: RootState) =>
  state.ginSteps.ginStepForm;

// Map selectors - using pre-computed maps from backend
export const selectStepsById = (state: RootState) =>
  state.ginSteps.ginStepForm?.steps_map;

export const selectStepsForCustomClient = (state: RootState) => {
  var steps: GinStep[] = [];
  var stepCount = 0;
  state.ginSteps.ginStepForm?.pages?.forEach((p) =>
    p.steps
      ? steps.push(
          ...p.steps.map((s) => {
            stepCount++;
            return {
              ...s,
              page_order: stepCount,
            };
          }),
        )
      : null,
  );
  steps = steps.filter((step) => getGinStepAssignedContactIds(step).length > 0);
  return steps;
};

export const selectPagesById = (state: RootState) =>
  state.ginSteps.ginStepForm?.pages_map;

export const selectItemsById = (state: RootState) =>
  state.ginSteps.ginStepForm?.items_map;

export const selectApplicationItemCreated = (state: RootState) =>
  state.ginSteps.applicationItemCreated;

export const selectStepIdsByPageId = createSelector(
  [selectGinStepForm],
  (ginForm) => {
    const map: Record<string, string[]> = {};
    ginForm?.pages?.forEach((page: GinStepPage) => {
      if (page.steps == null) return;
      map[page.page_id] = page.steps.map((s: GinStep) => s.step_id);
    });
    return map;
  },
);

export const selectItemIdsByStepId = createSelector(
  [selectGinStepForm],
  (ginForm) => {
    const map: Record<string, string[]> = {};
    ginForm?.pages?.forEach((page: GinStepPage) => {
      if (page.steps == null) return;
      page.steps.forEach((step: GinStep) => {
        map[step.step_id] =
          step.items?.map((i: GinStepItem) => i.item_id) ?? [];
      });
    });
    return map;
  },
);

export const {
  selectAll: selectAllGinStepAssets,
  selectById: selectGinStepAssetById,
} = ginStepAssetsAdapter.getSelectors(
  (state: RootState) => state.ginSteps.ginStepAssets,
);

export const {
  selectAll: selectAllGinStepTasks,
  selectById: selectGinStepTaskById,
} = ginStepTasksAdapter.getSelectors(
  (state: RootState) => state.ginSteps.ginStepTasks,
);

export const selectIsGinStepAssetsLoading = (state: RootState) =>
  state.ginSteps.ginStepAssetsStatus === 'pending';

export const ginStepsReducer = ginStepsSlice.reducer;
